/**
 * FloorplanToolBase - Base class for floorplan-related tools
 *
 * Provides common functionality for tools that work with floor selections:
 * - Floor selection event handling
 * - Floor isolation activation via FloorIsolationManager
 * - Floor/ceiling data tracking
 *
 * Subclasses should:
 * 1. Call super.onEnable() and super.onDisable()
 * 2. Override onFloorChanged() to react to floor selection changes
 * 3. Override getIsolationStrategy() to customise clip height
 * 4. Access floor data via protected properties (currentFloorFloorPlane, etc.)
 */

import { BaseTool } from '../../BaseTool';
import { DetectedFloorPlane } from '../../../utils/floorDetection';
import { defaultFloorIsolationStrategy, defaultIsolationSpec, boundsFromPlanes, loadFloorFootprintCorners, IsolationSpec } from '../../../utils/FloorIsolation';
import { FloorBarsPanelController } from '../modules/FloorBarsPanelController';
import { FloorplanPersona } from '../../personas/implementations/FloorplanPersona';

export abstract class FloorplanToolBase extends BaseTool {
  /**
   * Whether the tool needs a floor selection to operate. The persona greys
   * the toolbar button out (and auto-switches away from the tool) while no
   * floor is selected. Tools that merely ADAPT to the selected floor but work
   * fine without one (e.g. camera_orientation) override this to `false` —
   * every floor accessor below is already null-safe.
   */
  readonly requiresFloor: boolean = true;

  // Floor isolation state
  protected currentFloorFloorPlane: DetectedFloorPlane | null = null;
  protected currentFloorCeilingPlane: DetectedFloorPlane | null = null;
  protected currentFloorGroupIdentifier: string | null = null;
  protected currentFloorRoomIdentifiers: string[] = [];

  // Elevation-map bars: each tab's SELECTION tool shows the project's floors
  // on the left edge; the active floor is highlighted, clicking a bar
  // switches floors (via select_group) while the tool keeps doing its own
  // job.
  private floorBarsPanel: FloorBarsPanelController | null = null;

  /**
   * Opt-in per tool. The selection tools (room/wall/opening/structure
   * selection) override to `true`; creation/edit tools stay uncluttered.
   */
  protected showsFloorBars(): boolean {
    return false;
  }

  protected onEnable(): void {
    // Reset floor state
    this.currentFloorGroupIdentifier = null;
    this.currentFloorRoomIdentifiers = [];

    // Get initial floor selection from persona
    const floorplanPersona = this.persona as FloorplanPersona;
    const selectedFloorData = floorplanPersona.getSelectedFloorData();
    const selectedFloorGroupIdentifier = floorplanPersona.getSelectedFloorGroupIdentifier();

    if (selectedFloorData && selectedFloorGroupIdentifier) {
      this.currentFloorGroupIdentifier = selectedFloorGroupIdentifier;
      this.currentFloorRoomIdentifiers = selectedFloorData.roomIdentifiers || [];
      this.applyFloorIsolation(selectedFloorData.floor, selectedFloorData.ceiling);
      this.onFloorChanged(selectedFloorData.floor, selectedFloorData.ceiling, selectedFloorGroupIdentifier);
    }

    // Listen for selection changes from persona
    this.persona.addEventListener('floor_selection_changed', this.handleSelectionChanged);

    // Listen for view changes to update floor isolation clipping (via ViewManager)
    this.viewer.personaManager?.viewManager.addEventListener('view_changed', this.handleViewChanged);

    // Elevation-map bars for quick floor orientation/switching. These tools
    // are about rooms/walls/…, not the floors themselves, so the panel starts
    // minimized to its icon; only the floor tools open it by default.
    if (this.showsFloorBars()) {
      this.floorBarsPanel ??= new FloorBarsPanelController(this.viewer, this.persona, { defaultCollapsed: true });
      this.floorBarsPanel.enable();
    }
  }

  protected onDisable(): void {
    // Remove selection listener
    this.persona.removeEventListener('floor_selection_changed', this.handleSelectionChanged);
    this.viewer.personaManager?.viewManager.removeEventListener('view_changed', this.handleViewChanged);
    this.floorBarsPanel?.disable();

    // Clear isolation owned by this tool session
    this.clearFloorIsolation();
  }

  /**
   * Re-apply floor isolation when view mode changes. Subclasses whose strategy
   * depends on view should override `getIsolationStrategy` and rely on this
   * reapplying.
   */
  private handleViewChanged = (): void => {
    if (this.currentFloorFloorPlane) {
      this.applyFloorIsolation(this.currentFloorFloorPlane, this.currentFloorCeilingPlane);
      this.onFloorChanged(this.currentFloorFloorPlane, this.currentFloorCeilingPlane, this.currentFloorGroupIdentifier);
    }
  };

  private handleSelectionChanged = (event: { selectedFloorGroupIdentifier: string | null; floorData: { floor: DetectedFloorPlane; ceiling: DetectedFloorPlane | null; roomIdentifiers?: string[] } | null }): void => {
    const selectedFloorGroupIdentifier = event.selectedFloorGroupIdentifier;
    const floorData = event.floorData;

    if (selectedFloorGroupIdentifier && floorData) {
      this.currentFloorGroupIdentifier = selectedFloorGroupIdentifier;
      this.currentFloorRoomIdentifiers = floorData.roomIdentifiers || [];
      this.applyFloorIsolation(floorData.floor, floorData.ceiling);
      this.onFloorChanged(floorData.floor, floorData.ceiling, selectedFloorGroupIdentifier);
    } else {
      this.clearFloorIsolation();
      this.currentFloorGroupIdentifier = null;
      this.currentFloorRoomIdentifiers = [];
      this.onFloorChanged(null, null, null);
    }
  };

  /**
   * Hook for subclasses to react to floor selection changes.
   */
  protected onFloorChanged(_floor: DetectedFloorPlane | null, _ceiling: DetectedFloorPlane | null, _groupIdentifier: string | null): void {
    // Default: do nothing
  }

  /**
   * The per-view isolation spec this tool uses. Default: fixed-height in 3D,
   * full-floor slab in X-Ray (`defaultIsolationSpec`). Override to customise.
   * Subclasses can rely on `currentFloorGroupIdentifier` / `currentFloorFloorPlane`
   * being set by `applyFloorIsolation` before this is called.
   */
  protected getIsolationSpec(): IsolationSpec {
    const floorGroupId = this.currentFloorGroupIdentifier;
    if (!floorGroupId) {
      throw new Error('FloorplanToolBase.getIsolationSpec: no floor group selected');
    }
    if (!this.currentFloorFloorPlane) {
      return { base: defaultFloorIsolationStrategy(floorGroupId) };
    }
    const bounds = this.viewer.floorIsolationManager.resolveLevelBounds(floorGroupId) ?? boundsFromPlanes(floorGroupId, this.currentFloorFloorPlane, this.currentFloorCeilingPlane);
    return defaultIsolationSpec(bounds, loadFloorFootprintCorners(floorGroupId));
  }

  /**
   * Push the tool's preferred per-view spec to the manager.
   *
   * `floor`/`ceiling` are stored before resolving the spec so a subclass
   * `getIsolationSpec()` that reads `currentFloorFloorPlane` sees the new values.
   */
  protected applyFloorIsolation(floor: DetectedFloorPlane, ceiling: DetectedFloorPlane | null): void {
    this.currentFloorFloorPlane = floor;
    this.currentFloorCeilingPlane = ceiling;

    if (!this.currentFloorGroupIdentifier) return;

    this.viewer.floorIsolationManager.setCustomSpec(this.getIsolationSpec(), this.currentFloorGroupIdentifier);
  }

  /**
   * Z-range (center + tolerance) of the MAIN view's isolation slab for this
   * tool's floor, or null if a different floor (or none) is isolated. Callers
   * read it for segmentation depth filtering.
   */
  protected getFloorIsolationZRange(): { z: number; tolerance: number } | null {
    if (!this.currentFloorGroupIdentifier) return null;
    const manager = this.viewer.floorIsolationManager;
    if (manager.getActiveFloorGroupId() !== this.currentFloorGroupIdentifier) return null;
    return manager.getMainViewSlabZRange();
  }

  /**
   * Drop the current tool-driven clip without leaving custom mode, so the
   * next tool in the same persona can push its own strategy.
   */
  protected clearFloorIsolation(): void {
    this.viewer.floorIsolationManager.clearCustomStrategy();
    this.currentFloorFloorPlane = null;
    this.currentFloorCeilingPlane = null;
  }
}
